/**
 * Apps list page component.
 * Only accessible in preview mode.
 * Apps are sandboxes with the type=app label.
 *
 * @module pages/AppsPage
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Search,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Square,
  Play,
  ExternalLink,
} from 'lucide-react';
import { LoadingPage, Badge, ConfirmModal } from '@/components/ui';
import { useApps, useAppsCount, useDeleteApp, useStopApp, useResumeApp } from '@/hooks/useApps';
import { appService } from '@/services/appService';
import { useQueryClient } from '@tanstack/react-query';
import type { SandboxResponse } from '@/types';

/**
 * Apps list page
 */
export function AppsPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const { data: apps, isLoading } = useApps(page, pageSize);
  const { data: total = 0 } = useAppsCount();
  const deleteMutation = useDeleteApp();
  const stopMutation = useStopApp();
  const resumeMutation = useResumeApp();
  const queryClient = useQueryClient();
  const [deleteTarget, setDeleteTarget] = useState<SandboxResponse | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState({
    current: 0,
    total: 0,
    currentId: '',
  });
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Filter apps based on search query
  const filteredApps = apps?.filter((app) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();

    // Search in app ID
    if (app.id.toLowerCase().includes(query)) {
      return true;
    }

    // Search in labels (keys and values)
    if (app.labels) {
      const labelsMatch = Object.entries(app.labels as Record<string, string>).some(
        ([key, value]) => key.toLowerCase().includes(query) || value.toLowerCase().includes(query)
      );
      if (labelsMatch) {
        return true;
      }
    }

    return false;
  });

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id, {
        onSuccess: () => setDeleteTarget(null),
      });
    }
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handleFirstPage = () => {
    setPage(1);
  };

  const handleLastPage = () => {
    setPage(totalPages);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1); // Reset to first page when changing page size
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && filteredApps) {
      setSelectedIds(new Set(filteredApps.map((s) => s.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    setIsBulkDeleting(true);
    const idsToDelete = Array.from(selectedIds);
    setBulkDeleteProgress({ current: 0, total: idsToDelete.length, currentId: '' });

    const BATCH_SIZE = 5;
    let completed = 0;

    // Process in batches of 5
    for (let i = 0; i < idsToDelete.length; i += BATCH_SIZE) {
      const batch = idsToDelete.slice(i, i + BATCH_SIZE);

      // Run batch in parallel
      await Promise.all(
        batch.map(async (id) => {
          try {
            await appService.delete(id);
            completed++;
            setBulkDeleteProgress({
              current: completed,
              total: idsToDelete.length,
              currentId: batch.length > 1 ? `Batch ${Math.floor(i / BATCH_SIZE) + 1}` : id,
            });
          } catch (error) {
            console.error(`Failed to delete app ${id}:`, error);
            completed++;
            setBulkDeleteProgress({
              current: completed,
              total: idsToDelete.length,
              currentId: batch.length > 1 ? `Batch ${Math.floor(i / BATCH_SIZE) + 1}` : id,
            });
          }
        })
      );
    }

    // Invalidate queries to refresh the list
    queryClient.invalidateQueries({ queryKey: ['apps'] });

    setBulkDeleteProgress({
      current: idsToDelete.length,
      total: idsToDelete.length,
      currentId: '',
    });
    setIsBulkDeleting(false);
    setSelectedIds(new Set());

    // Close modal after a brief delay to show completion
    setTimeout(() => {
      setShowBulkDeleteModal(false);
      setBulkDeleteProgress({ current: 0, total: 0, currentId: '' });
    }, 1000);
  };

  const handleDeleteAll = async () => {
    if (total === 0) return;

    setIsBulkDeleting(true);

    // Fetch all app IDs using the service to ensure proper authentication
    const allIds: string[] = [];
    const totalPages = Math.ceil(total / 100); // Fetch in batches of 100

    try {
      // Fetch all app IDs
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const appsPage = await appService.list(pageNum, 100);
        allIds.push(...appsPage.map((s) => s.id));
      }
    } catch (error) {
      console.error('Failed to fetch all apps:', error);
      setIsBulkDeleting(false);
      return;
    }

    setBulkDeleteProgress({ current: 0, total: allIds.length, currentId: '' });

    const BATCH_SIZE = 5;
    let completed = 0;

    // Process in batches of 5
    for (let i = 0; i < allIds.length; i += BATCH_SIZE) {
      const batch = allIds.slice(i, i + BATCH_SIZE);

      // Run batch in parallel
      await Promise.all(
        batch.map(async (id) => {
          try {
            await appService.delete(id);
            completed++;
            setBulkDeleteProgress({
              current: completed,
              total: allIds.length,
              currentId: batch.length > 1 ? `Batch ${Math.floor(i / BATCH_SIZE) + 1}` : id,
            });
          } catch (error) {
            console.error(`Failed to delete app ${id}:`, error);
            completed++;
            setBulkDeleteProgress({
              current: completed,
              total: allIds.length,
              currentId: batch.length > 1 ? `Batch ${Math.floor(i / BATCH_SIZE) + 1}` : id,
            });
          }
        })
      );
    }

    // Invalidate queries to refresh the list
    queryClient.invalidateQueries({ queryKey: ['apps'] });

    setBulkDeleteProgress({ current: allIds.length, total: allIds.length, currentId: '' });
    setIsBulkDeleting(false);

    // Close modal after a brief delay to show completion
    setTimeout(() => {
      setShowDeleteAllModal(false);
      setBulkDeleteProgress({ current: 0, total: 0, currentId: '' });
    }, 1000);
  };

  const isAllSelected =
    filteredApps && filteredApps.length > 0 && filteredApps.every((s) => selectedIds.has(s.id));

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Apps</h1>
        <div className="flex items-center gap-3">
          <Link to="/apps/new">
            <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 rounded-lg transition-colors">
              <Plus className="h-4 w-4" />
              Create App
            </button>
          </Link>
        </div>
      </div>

      {/* Controls bar */}
      <div className="flex items-center gap-3 mb-3">
        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-600" />
          <input
            type="text"
            placeholder="Search by ID or labels"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search apps by ID or labels"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-[#2a2a2a] dark:bg-[#161616] dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="mb-3 flex items-center gap-3 px-4 py-2 bg-gray-50 dark:bg-[#161616] border border-gray-200 dark:border-[#2a2a2a] rounded-lg">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {selectedIds.size} app{selectedIds.size > 1 ? 's' : ''} selected
          </span>
          <button
            onClick={() => setShowBulkDeleteModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            Delete Selected
          </button>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg overflow-hidden flex flex-col">
        <div className="flex-1 overflow-auto">
          <table className="w-full min-w-max" aria-label="Apps">
            <thead className="bg-white dark:bg-[#0a0a0a] border-b border-gray-200 dark:border-[#2a2a2a]">
              <tr>
                <th className="px-4 py-1.5 text-left">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    aria-label="Select all apps"
                    className="rounded border-gray-300 dark:border-gray-700 dark:bg-[#161616]"
                  />
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  ID
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Labels
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  State
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  App URL
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Actions
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Snapshot
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Resources
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Created At
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-[#0a0a0a] divide-y divide-gray-200 dark:divide-[#2a2a2a]">
              {!filteredApps || filteredApps.length === 0 ? (
                <tr className="bg-white dark:bg-[#0a0a0a]">
                  <td colSpan={9} className="px-4 py-16">
                    <div className="flex flex-col items-center justify-center text-center">
                      <Box className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
                      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-1">
                        {searchQuery ? 'No matching apps found.' : 'No Apps yet.'}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                        {searchQuery
                          ? 'Try adjusting your search query.'
                          : 'Create an App to run services with on-demand activation.'}
                      </p>
                      {!searchQuery && (
                        <Link to="/apps/new">
                          <button className="mt-4 flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 rounded-lg transition-colors">
                            <Plus className="h-4 w-4" />
                            Create App
                          </button>
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredApps.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50 dark:hover:bg-[#161616]">
                    <td className="px-4 py-1.5">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(app.id)}
                        onChange={(e) => handleSelectOne(app.id, e.target.checked)}
                        aria-label={`Select app ${app.id}`}
                        className="rounded border-gray-300 dark:border-gray-700 dark:bg-[#161616]"
                      />
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap">
                      <Link
                        to={`/apps/${encodeURIComponent(app.id)}`}
                        className="text-sm font-medium text-gray-900 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400"
                      >
                        {app.id}
                      </Link>
                    </td>
                    <td className="px-4 py-1.5 text-sm text-gray-600 dark:text-gray-400">
                      {app.labels && Object.keys(app.labels).length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(app.labels as Record<string, string>).map(
                            ([key, value]) => (
                              <span
                                key={key}
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                              >
                                {key}: {value}
                              </span>
                            )
                          )}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap">
                      <Badge variant={app.state === 'Stopped' ? 'warning' : 'neutral'}>
                        {app.state || 'Running'}
                      </Badge>
                    </td>
                    <td className="px-4 py-1.5">
                      {app.appUrl ? (
                        <a
                          href={app.appUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                          title={app.appUrl}
                        >
                          Open
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {app.state === 'Stopped' ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              resumeMutation.mutate(app.id);
                            }}
                            disabled={resumeMutation.isPending}
                            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-[#161616] disabled:opacity-50"
                            title="Resume app"
                            aria-label={`Resume app ${app.id}`}
                          >
                            <Play className="h-4 w-4 text-green-600 fill-green-600" />
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              stopMutation.mutate(app.id);
                            }}
                            disabled={stopMutation.isPending}
                            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-[#161616] disabled:opacity-50"
                            title="Stop app"
                            aria-label={`Stop app ${app.id}`}
                          >
                            <Square className="h-4 w-4 text-red-600 fill-red-600" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {app.snapshotId || '-'}
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {app.resources?.cpu && app.resources?.memory
                        ? `${app.resources.cpu} CPU / ${app.resources.memory}`
                        : '-'}
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {app.createdAt ? new Date(app.createdAt).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        <div className="flex items-center justify-between px-6 py-2 border-t border-gray-200 dark:border-[#2a2a2a] bg-white dark:bg-[#0a0a0a]">
          <div className="flex items-center gap-4">
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              aria-label="Apps per page"
              className="text-sm border border-gray-300 dark:border-[#2a2a2a] dark:bg-[#161616] dark:text-white rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {searchQuery && filteredApps && filteredApps.length !== total ? (
                <>
                  Showing {filteredApps.length} of {total} apps (filtered)
                </>
              ) : (
                <>
                  Showing {apps && apps.length > 0 ? (page - 1) * pageSize + 1 : 0} to{' '}
                  {Math.min(page * pageSize, total)} of {total} apps
                </>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={handleFirstPage}
              disabled={page <= 1}
              aria-label="First page"
              className="px-2 py-1.5 text-xs rounded hover:bg-gray-100 dark:hover:bg-[#161616] disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 dark:text-gray-400"
            >
              First
            </button>
            <button
              onClick={handlePreviousPage}
              disabled={page <= 1}
              aria-label="Previous page"
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-[#161616] disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 dark:text-gray-400"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={handleNextPage}
              disabled={page >= totalPages}
              aria-label="Next page"
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-[#161616] disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 dark:text-gray-400"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              onClick={handleLastPage}
              disabled={page >= totalPages}
              aria-label="Last page"
              className="px-2 py-1.5 text-xs rounded hover:bg-gray-100 dark:hover:bg-[#161616] disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 dark:text-gray-400"
            >
              Last
            </button>
          </div>
        </div>
      </div>

      {/* Delete All Button */}
      {total > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setShowDeleteAllModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            Delete All ({total})
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete App"
        message={`Are you sure you want to delete app "${deleteTarget?.id}"? This action cannot be undone and any data in the app will be lost.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Delete ${selectedIds.size} apps`}
            className="bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg shadow-xl max-w-md w-full mx-4"
          >
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Delete {selectedIds.size} App{selectedIds.size > 1 ? 's' : ''}?
              </h2>

              {!isBulkDeleting ? (
                <>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Are you sure you want to delete {selectedIds.size} app
                    {selectedIds.size > 1 ? 's' : ''}? This action cannot be undone and any data in
                    the apps will be lost.
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowBulkDeleteModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#161616] border border-gray-300 dark:border-[#2a2a2a] rounded-lg hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                    >
                      Delete All
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <span>Deleting apps...</span>
                      <span>
                        {bulkDeleteProgress.current} / {bulkDeleteProgress.total}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-[#161616] rounded-full h-2.5">
                      <div
                        className="bg-red-600 h-2.5 rounded-full transition-all duration-300"
                        style={{
                          width: `${(bulkDeleteProgress.current / bulkDeleteProgress.total) * 100}%`,
                        }}
                      ></div>
                    </div>
                    {bulkDeleteProgress.currentId && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Deleting: {bulkDeleteProgress.currentId}
                      </p>
                    )}
                  </div>
                  {bulkDeleteProgress.current === bulkDeleteProgress.total && (
                    <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                      ✓ All apps deleted successfully
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete All Modal */}
      {showDeleteAllModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Delete all apps"
            className="bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg shadow-xl max-w-md w-full mx-4"
          >
            <div className="p-6">
              <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-4">
                ⚠️ Delete All {total} Apps?
              </h2>

              {!isBulkDeleting ? (
                <>
                  <p className="text-gray-600 dark:text-gray-400 mb-2">
                    This will permanently delete <strong>ALL {total} apps</strong> across all pages.
                  </p>
                  <p className="text-red-600 dark:text-red-400 text-sm font-medium mb-6">
                    This action cannot be undone and all data will be lost!
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowDeleteAllModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#161616] border border-gray-300 dark:border-[#2a2a2a] rounded-lg hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteAll}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                    >
                      Delete All {total} Apps
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <span>Deleting all apps...</span>
                      <span>
                        {bulkDeleteProgress.current} / {bulkDeleteProgress.total}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-[#161616] rounded-full h-2.5">
                      <div
                        className="bg-red-600 h-2.5 rounded-full transition-all duration-300"
                        style={{
                          width: `${(bulkDeleteProgress.current / bulkDeleteProgress.total) * 100}%`,
                        }}
                      ></div>
                    </div>
                    {bulkDeleteProgress.currentId && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Deleting: {bulkDeleteProgress.currentId}
                      </p>
                    )}
                  </div>
                  {bulkDeleteProgress.current === bulkDeleteProgress.total && (
                    <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                      ✓ All apps deleted successfully
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
